/**
 * rebuildPuckFromGoals — single source of truth for how a `Puck`'s
 * `startMs` / `endMs` / `primaryGoal` / `constituentGoals` are derived
 * from a set of goals. Used by `goalMerger.buildPuck`, `collapsePucks`,
 * and `dropCanceledSiblings` so any future tweak to the derivation
 * lives in one place.
 *
 * Callers own the puck `id` (goalMerger uses a sorted+joined format;
 * post-merge transforms preserve the input puck's id for timeline
 * stability), the optional `dockDoorId` override, and whether to set
 * `originalConstituentGoals` on the output.
 *
 * @see .claude/docs/puck-behavior-matrix.md §13 delta D2
 */

import { Intent, type Goal } from "@/features/ryteTracker/types/goal";
import type { Puck } from "@/features/ryteTracker/types/puck";
import { isTerminalStatus } from "@/features/ryteTracker/utils/vocabulary/goalStatusSets";

/**
 * 2 hours — single source of truth for goal-duration fallback when a
 * concrete `delayedAfter` is required (e.g. drag write-back's
 * `UpdateGoal` payload). NOT used by the render path — see
 * `goalRenderEndMs` below for the no-floor variant.
 */
export const DEFAULT_GOAL_DURATION_MS = 2 * 60 * 60 * 1000;

/**
 * Sentinel for REMOVE goals missing both `start` and `delayedAfter`.
 * 2126-01-01 UTC — far enough out that no real schedule lands here, near
 * enough that "year 2126" reads as obviously fake in logs/devtools. Sorts
 * malformed REMOVEs to the end of a puck so the LOAD/UNLOAD wins
 * `primaryGoal` selection. `isValidGoal` filters such REMOVEs out
 * upstream — this is defense-in-depth.
 */
const REMOVE_MISSING_TIME_SENTINEL_MS = 4_924_713_600_000;

/**
 * Extract start ms from a goal, defaulting to 0 when missing.
 *
 * REMOVE-aware: current REMOVE writes omit `executionWindow.start`, so
 * falling back to `?? 0` would sort every REMOVE to epoch and break
 * merged-puck `primaryGoal` selection, `puck.startMs = Math.min(...)`, and
 * the slice-level `sortComparer`. For REMOVEs, fall back to `delayedAfter`
 * (their canonical schedule time) and only then to the year-2126 sentinel
 * for the pathological "neither field present" case. Legacy REMOVEs that still
 * carry `start` take the first arm — backward-compatible.
 */
export const goalStartMs = (g: Goal): number => {
  const ew = g.parameters?.executionWindow;
  if (g.intent === Intent.REMOVE) {
    return ew?.start ?? ew?.delayedAfter ?? REMOVE_MISSING_TIME_SENTINEL_MS;
  }
  return ew?.start ?? 0;
};

/**
 * Extract end ms from a goal **for write-back paths that need a
 * concrete `delayedAfter` value** — e.g. `buildUpdatePlansFromDrag`
 * which threads this into the `UpdateGoal` API payload. Falls back to
 * `start + DEFAULT_GOAL_DURATION_MS` (2h) when `delayedAfter` is
 * missing so the engine receives a reasonable default rather than an
 * undefined.
 *
 * **Render paths use `goalRenderEndMs` instead.** The 2h fallback was
 * baking a minimum length into the puck's `endMs` that overrode the
 * projection's open-ended-extension rule via `Math.max` "later-wins"
 * composition. The `rebuildPuckFromGoals` test "open-ended LOAD without
 * delayedAfter" guards this behavior.
 */
export const goalEndMs = (g: Goal): number => {
  const start = goalStartMs(g);
  return g.parameters?.executionWindow?.delayedAfter ?? start + DEFAULT_GOAL_DURATION_MS;
};

/**
 * Extract end ms from a goal **for the render path**: returns
 * `delayedAfter` when present, otherwise `start` (i.e. zero-length
 * goal). Returning `start` rather than `start + 2h` lets the
 * projection's open-ended-extension rules
 * (`completedWithoutRemovalRule`, `failModeExtensionRule`,
 * `removeInProgressExtensionRule`) extend the puck's right edge to the
 * next-neighbor cap or 72h fallback without being overridden by a
 * baked-in 2h minimum via `Math.max(currentEndMs, candidateEndMs)`
 * inside `extendPillToNeighborOrFallback`.
 *
 * The 2h fallback is preserved on `goalEndMs` for drag write-back
 * (the engine still expects a concrete `delayedAfter` in the
 * `UpdateGoal` payload). Splitting the two helpers preserves the
 * write-back semantic while unblocking the render-side extension
 * rules.
 *
 * Affects production XD pucks where the engine emits a LOAD with no
 * `delayedAfter` (per the engine model — XD LOAD has no ACTIVE state
 * and no observed dock-processing window, limitation #3 of
 * `project_xd_api_model.md`).
 */
export const goalRenderEndMs = (g: Goal): number => {
  const start = goalStartMs(g);
  return g.parameters?.executionWindow?.delayedAfter ?? start;
};

interface RebuildPuckOptions {
  /** Stable puck ID. Callers own the ID format. */
  id: string;
  /** Component goals. Must be non-empty. */
  componentGoals: Goal[];
  /**
   * Optional dock door override. When omitted, derived from the earliest
   * goal's first location (same as `goalMerger.buildPuck`).
   */
  dockDoorId?: string;
  /**
   * Optional full pre-transform goal list to preserve on the returned
   * puck as `originalConstituentGoals` (detail-panel parity).
   */
  originalConstituentGoals?: Goal[];
  /**
   * Optional base puck to spread additional fields from (e.g.
   * `plannedStartMs`, `plannedEndMs`, etc.). Fields owned by this
   * helper — `id`, `constituentGoals`, `primaryGoal`, `dockDoorId`,
   * `startMs`, `endMs`, `originalConstituentGoals` — are always
   * overwritten.
   */
  base?: Puck;
}

/**
 * Rebuilds the puck-shape fields from `componentGoals`:
 *   - sorts by `executionWindow.start` (stable; nullish → 0; REMOVE
 *     falls back to `delayedAfter` via `goalStartMs` shim)
 *   - `primaryGoal` = first after sort
 *   - `startMs` = min over component starts
 *   - `endMs` = max over non-terminal component ends (`goalRenderEndMs`),
 *     clamped to ≥ startMs by the trailing `Math.max(endMs, startMs)`
 *     invariant. Falls back to all-goals max when every survivor is
 *     terminal (defensive — the puck is filtered downstream by
 *     `dropCanceledRule` / status overrides anyway).
 *   - `dockDoorId` = override ?? primary's first location
 *
 * Returns a `Puck` ready to slot into the pipeline. `componentGoals`
 * must be non-empty; callers are responsible for filtering upstream.
 *
 * The former REMOVE-specific endMs branch picked the lone non-terminal
 * REMOVE and emitted `Math.max(start, delayedAfter)` as a special anchor.
 * That became redundant when REMOVEs began carrying only `delayedAfter`
 * on the wire. Every REMOVE now
 * contributes `delayedAfter` (or its `start` if legacy-shaped) to the
 * `endMs` Math.max directly via `goalRenderEndMs`, producing the same
 * right edge without the dedicated branch.
 */
export const rebuildPuckFromGoals = ({
  id,
  componentGoals,
  dockDoorId,
  originalConstituentGoals,
  base,
}: RebuildPuckOptions): Puck => {
  const sorted = [...componentGoals].sort((a, b) => goalStartMs(a) - goalStartMs(b));
  const primaryGoal = sorted[0];
  const startMs = Math.min(...sorted.map(goalStartMs));
  const nonTerminalGoals = sorted.filter((g) => !isTerminalStatus(g.status));
  const endMs =
    nonTerminalGoals.length > 0
      ? Math.max(...nonTerminalGoals.map(goalRenderEndMs))
      : Math.max(...sorted.map(goalRenderEndMs));
  const resolvedDockDoorId = dockDoorId ?? primaryGoal.parameters?.locations?.[0]?.id ?? "";

  return {
    ...(base ?? {}),
    id,
    constituentGoals: sorted,
    primaryGoal,
    dockDoorId: resolvedDockDoorId,
    startMs,
    // Invariant: endMs >= startMs always. The clamp covers (a) the
    // legacy "all-terminal puck" fallback path and (b) any REMOVE shape
    // whose contributions to legacyEndMs land below the puck's startMs.
    endMs: Math.max(endMs, startMs),
    ...(originalConstituentGoals ? { originalConstituentGoals } : {}),
  };
};
